import api from "./client";

// Headlines API
interface LocalizedText {
  de: string;
  en: string;
}

export const headlinesApi = {
  list: (variant?: string, section?: string) =>
    api.get("/admin/cms/headlines", { params: { variant, section } }),
  create: (data: {
    variant: string;
    section: string;
    headline: LocalizedText;
    subheadline: LocalizedText;
    ctaText: LocalizedText;
    weight?: number;
  }) => api.post("/admin/cms/headlines", data),
  update: (id: string, data: Partial<{
    section: string;
    headline: LocalizedText;
    subheadline: LocalizedText;
    ctaText: LocalizedText;
    isActive: boolean;
    weight: number;
  }>) => api.put(`/admin/cms/headlines/${id}`, data),
  delete: (id: string) => api.delete(`/admin/cms/headlines/${id}`),
};

// Testimonials API
export const testimonialsApi = {
  list: () => api.get("/admin/cms/testimonials"),
  create: (data: {
    name: string;
    location: string;
    amount: number;
    quote: string;
    rating?: number;
    variant?: string | null;
  }) => api.post("/admin/cms/testimonials", data),
  update: (id: string, data: Partial<{
    name: string;
    location: string;
    amount: number;
    quote: string;
    rating: number;
    variant: string | null;
    isActive: boolean;
  }>) => api.put(`/admin/cms/testimonials/${id}`, data),
  delete: (id: string) => api.delete(`/admin/cms/testimonials/${id}`),
};

// FAQ API
export const faqApi = {
  list: () => api.get("/admin/cms/faq"),
  create: (data: {
    category: string;
    question: string;
    answer: string;
    order?: number;
  }) => api.post("/admin/cms/faq", data),
  update: (id: string, data: Partial<{
    category: string;
    question: string;
    answer: string;
    order: number;
    isActive: boolean;
  }>) => api.put(`/admin/cms/faq/${id}`, data),
  delete: (id: string) => api.delete(`/admin/cms/faq/${id}`),
  reorder: (items: { id: string; order: number }[]) =>
    api.post("/admin/cms/faq/reorder", { items }),
};

// Media API
export const mediaApi = {
  list: () => api.get("/admin/cms/media"),
  get: (key: string) => api.get(`/admin/cms/media/${key}`),
  create: (data: {
    key: string;
    title: string;
    description?: string | null;
    embedUrl: string;
    platform?: string;
    thumbnailUrl?: string | null;
    duration?: number | null;
    placement: string;
    isActive?: boolean;
  }) => api.post("/admin/cms/media", data),
  update: (key: string, data: {
    title?: string;
    description?: string | null;
    embedUrl?: string;
    platform?: string;
    thumbnailUrl?: string | null;
    duration?: number | null;
    placement?: string;
    isActive?: boolean;
  }) => api.put(`/admin/cms/media/${key}`, data),
  delete: (key: string) => api.delete(`/admin/cms/media/${key}`),
  init: () => api.post("/admin/cms/media/init"),
};

// Updates API
export const updatesApi = {
  list: () => api.get("/admin/cms/updates"),
  get: (id: string) => api.get(`/admin/cms/updates/${id}`),
  create: (data: {
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    category: string;
    publishedAt?: string | null;
    imageUrl?: string | null;
    videoUrl?: string | null;
    wistiaId?: string | null;
  }) => api.post("/admin/cms/updates", data),
  update: (id: string, data: Partial<{
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    category: string;
    publishedAt: string | null;
    imageUrl: string | null;
    videoUrl: string | null;
    wistiaId: string | null;
    isPublished: boolean;
  }>) => api.put(`/admin/cms/updates/${id}`, data),
  delete: (id: string) => api.delete(`/admin/cms/updates/${id}`),
  publish: (id: string, publish: boolean) =>
    api.post(`/admin/cms/updates/${id}/publish`, { publish }),
  reorder: (items: { id: string; order: number }[]) =>
    api.post("/admin/cms/updates/reorder", { items }),
};

// Legal Disclaimer API
export const legalDisclaimerApi = {
  get: () => api.get("/admin/cms/legal-disclaimer"),
  update: (content: string) => api.put("/admin/cms/legal-disclaimer", { content }),
  init: () => api.post("/admin/cms/legal-disclaimer/init"),
};

// Site Stats API
export const siteStatsApi = {
  get: () => api.get("/admin/cms/stats"),
  update: (data: {
    totalPaidOut?: number;
    claimsProcessed?: number;
    averagePayout?: number;
    successRate?: number;
    processingTimeDays?: number;
  }) => api.put("/admin/cms/stats", data),
};

// Section Content API (repeatable items per section)
export const sectionContentApi = {
  list: (variant?: string, section?: string, type?: string) =>
    api.get("/admin/cms/section-content", { params: { variant, section, type } }),
  create: (data: {
    variant: string;
    section: string;
    type: string;
    title: LocalizedText;
    description?: LocalizedText;
    order?: number;
  }) => api.post("/admin/cms/section-content", data),
  update: (id: string, data: Partial<{
    title: LocalizedText;
    description: LocalizedText;
    order: number;
    isActive: boolean;
  }>) => api.put(`/admin/cms/section-content/${id}`, data),
  delete: (id: string) => api.delete(`/admin/cms/section-content/${id}`),
  reorder: (items: { id: string; order: number }[]) =>
    api.post("/admin/cms/section-content/reorder", { items }),
};
